import type { ResourceType } from 'mapbox-gl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Map, Marker, Popup, type MapRef, type ViewState } from 'react-map-gl'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import {
  aClient,
  aDrawMode,
  aDrawnFeature,
  aHoverFeature,
  aIsMapEasing,
  aMapFilters,
  aMapStyle,
  aMapViewState,
  aMarkLocation,
  aSelectedListing,
  useAtom,
  useAtomValue,
  useSetAtom,
  type SelectedListing,
} from '~/atoms'
import {
  CLUSTER_ZOOM_LEVEL,
  MAPBOX_ACCESS_TOKEN,
  STATUSES,
  TYPES,
} from '~/constants'
import { useBreakpoint, useIsSafari } from '~/hooks/browser'
import { MapView, useCurrentMapView, useMapIcons } from '~/hooks/map'
import { tryParseJson } from '~/lib'
import { getMapCardSizes } from '~/lib.client'

const MAP_STYLES: { [key: string]: string } = {
  street: 'mapbox://styles/mapbox/standard', // streets
  satellite: 'mapbox://styles/mapbox/standard-satellite',
}

type ListingMapProps = {
  mapRef: React.RefObject<MapRef>
  children: React.ReactNode
}

const zIndexLayerIds = ['z-index-0', 'z-index-1', 'z-index-2', 'z-index-3']
const SERVE_TILE_KEY = 'GmvvWhvAdlfwVFOwsalFJqCHHHunjTvVbXAbaBlpkrPwuKWLRdxpyXgnVZsUBYIN'

function isTileRequest(url: string) {
  return (
    (url.startsWith('http://localhost') ||
      url.startsWith('https://serve.resimplifi.com')) &&
    url.endsWith('.mvt')
  )
}

function transformRequest(url: string, resourceType: ResourceType) {
  if (resourceType !== 'Tile' || !isTileRequest(url)) return { url }

  return {
    headers: {
      'X-Serve-Tile-Key': SERVE_TILE_KEY,
    },
    url,
  }
}

export default function ListingsMap(props: ListingMapProps) {
  const { mapRef, children } = props
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const client = useAtomValue(aClient)
  const [mapFilters, setMapFilters] = useAtom(aMapFilters)
  const [mapViewState, setMapViewState] = useAtom(aMapViewState)
  const mapStyle = useAtomValue(aMapStyle)
  const currentMapView = useCurrentMapView()
  const [markLocation, setMarkLocation] = useAtom(aMarkLocation)
  const isMapEasing = useAtomValue(aIsMapEasing)
  const isSafari = useIsSafari()
  const setSelectedListing = useSetAtom(aSelectedListing)
  const [hoverFeature, setHoverFeature] = useAtom(aHoverFeature)
  const { breakpoint } = useBreakpoint()
  const drawMode = useAtomValue(aDrawMode)
  const drawFeature = useAtomValue(aDrawnFeature)
  const [readyMapStyle, setReadyMapStyle] = useState<string | null>(null)

  useMapIcons(mapRef, mapStyle)

  useEffect(() => {
    if (isSafari && mapRef.current && mapRef.current.getBearing() !== 0) {
      mapRef.current.setBearing(0)
      mapRef.current.setPitch(0)
    }
  }, [isSafari, mapRef])

  useEffect(() => {
    document.documentElement.style.overflowY = 'hidden'
    return () => {
      document.documentElement.style.overflowY = 'auto'
    }
  }, [])

  const addZIndexLayers = useCallback(() => {
    if (!mapRef.current) return

    const map = mapRef.current.getMap()

    zIndexLayerIds.forEach((id, index) => {
      if (map.getLayer(id)) {
        return
      }

      map.addLayer(
        { id, type: 'background', paint: { 'background-opacity': 0 } },
        index > 0 ? zIndexLayerIds[index - 1] : undefined
      )
    })
  }, [mapRef])

  const updateSearchParams = useCallback(() => {
    if (currentMapView === MapView.Map) {
      // const url = new URL(window.location.href)
      const center = mapRef.current?.getCenter()
      const zoom = mapRef.current!.getZoom()
      const bearing = mapRef.current!.getBearing()
      const pitch = mapRef.current!.getPitch()
      setMapFilters({
        ...mapFilters,
        lng: center!.lng.toFixed(6),
        lat: center!.lat.toFixed(6),
        z: zoom.toFixed(3),
        b: isSafari ? undefined : bearing.toFixed(3),
        p: isSafari ? undefined : pitch.toFixed(3),
      })
    }
  }, [currentMapView, mapFilters, mapRef, isSafari, setMapFilters])

  useEffect(() => {
    if (mapRef.current) {
      updateSearchParams()
    }
  }, [mapRef])

  const initialViewState: ViewState = useMemo(() => {
    const zoomAdjust = 0 //breakpoint === 'xs' ? 1 : 0
    let viewState = {
      latitude: client!.initial_state.latitude,
      longitude: client!.initial_state.longitude,
      zoom: client!.initial_state.zoom - zoomAdjust,
      padding: { left: getMapCardSizes().list, top: 0, right: 0, bottom: 0 },
      bearing: 0,
      pitch: 0,
    }
    if (searchParams.get('lng') && searchParams.get('lat')) {
      viewState = {
        ...viewState,
        latitude: parseFloat(searchParams.get('lat')!),
        longitude: parseFloat(searchParams.get('lng')!),
        zoom: searchParams.get('z')
          ? parseFloat(searchParams.get('z')!)
          : viewState.zoom,
      }
      if (!isSafari) {
        viewState.bearing = searchParams.get('b')
          ? parseFloat(searchParams.get('b')!)
          : viewState.bearing
        viewState.pitch = searchParams.get('p')
          ? parseFloat(searchParams.get('p')!)
          : viewState.bearing
      }
    }
    return viewState
  }, [searchParams, isSafari, client, breakpoint])

  const handleClick = useCallback(
    (e: mapboxgl.MapLayerMouseEvent | mapboxgl.MapLayerTouchEvent) => {
      if (!!drawMode || !!drawFeature) {
        return
      }
      const feature = (e.features || []).find(
        (l) => l.source === 'property-listings'
      )
      if (feature) {
        e.preventDefault()
        if (feature.properties!._id) {
          if (e.originalEvent.metaKey || e.originalEvent.ctrlKey) {
            window.open(`/listings/${feature.properties!.slug}`, '_blank')
          } else {
            navigate(`/${feature.properties!.slug}`)
            setSelectedListing({
              ...feature.properties,
              location: {
                // @ts-ignore
                coordinates: feature.geometry.coordinates,
              },
            } as SelectedListing)
          }
        } else {
          mapRef.current?.easeTo({
            // @ts-ignore
            center: feature.geometry.coordinates,
            zoom:
              mapViewState!.zoom < 8 && feature.properties?.point_count < 10
                ? CLUSTER_ZOOM_LEVEL - 1
                : CLUSTER_ZOOM_LEVEL + 1,
            padding: {
              left: getMapCardSizes().list,
              top: 0,
              bottom: 0,
              right: 0,
            },
            duration: 500,
          })
          setSelectedListing(null)
        }
      } else if (location.pathname !== '/') {
        navigate(`/${location.search}`)
        setSelectedListing(null)
      }
    },
    [currentMapView, location, mapViewState, drawMode, drawFeature]
  )

  return (
    <div id="map-container" className="relative h-dvh w-dvw">
      <Map
        ref={mapRef}
        key={mapStyle}
        initialViewState={initialViewState}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={MAP_STYLES[mapStyle]}
        // add projection mercator: zoom 5.5 (below 6) → globe → blank
        // ~6.5–6.7 (above 6) → flat → fine.
        projection={{ name: 'mercator' }}
        antialias={true}
        minZoom={4}
        maxZoom={18}
        interactiveLayerIds={['property-clusters', 'property-single-clusters']}
        style={{ width: '100%', height: '100%', userSelect: 'none' }}
        customAttribution="View by Resimplifi"
        dragRotate={!isSafari}
        onMouseMove={(e) => {
          const feature = e.features?.[0]
          if (!mapRef.current) return
          if (
            feature &&
            (feature.layer?.id === 'property-clusters' ||
              feature.layer?.id === 'property-single-clusters')
          ) {
            mapRef.current!.getMap().getCanvasContainer().style.cursor =
              'pointer'
          } else {
            mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
          }
        }}
        onMouseDown={() => {
          if (!mapRef.current) return
          mapRef.current!.getMap().getCanvasContainer().style.cursor =
            'grabbing'
        }}
        onMouseUp={() => {
          if (!mapRef.current) return
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
        }}
        onLoad={(e) => {
          addZIndexLayers()
          setReadyMapStyle(mapStyle)
          const { lat, lng } = e.target.getCenter()
          setMapViewState({
            bounds: e.target.getBounds()!,
            lat,
            lng,
            zoom: e.target.getZoom(),
          })
        }}
        onMoveEnd={(e) => {
          updateSearchParams()
          const { lat, lng } = e.target.getCenter()
          setMapViewState({
            bounds: e.target.getBounds()!,
            lat,
            lng,
            zoom: e.target.getZoom(),
          })
          if (!isMapEasing) {
            setMarkLocation(null)
          }
        }}
        onClick={(e) => handleClick(e)}
        onTouchEnd={(e) => handleClick(e)}
        onMouseEnter={(e) => {
          if (
            e.features &&
            e.features.length > 0 &&
            // @ts-ignore
            e.features![0].properties.lat
          ) {
            setHoverFeature(e.features![0].properties as any)
          }
        }}
        onMouseOut={() => {
          setHoverFeature(null)
        }}
        onMouseLeave={() => {
          setHoverFeature(null)
        }}
        transformRequest={transformRequest}>
        {readyMapStyle === mapStyle && (
          <>
            {markLocation && (
              <Marker
                latitude={markLocation.lat}
                longitude={markLocation.lng}
                color="#0284c7"
              />
            )}
            {hoverFeature && (
              <Popup
                latitude={hoverFeature.lat}
                longitude={hoverFeature.lng}
                style={{ zIndex: 30 }}
                className="disable-pointer-events">
                <p className="mb-1 text-sm font-medium">{hoverFeature.name}</p>
                {hoverFeature.address && (
                  <p className="text-sm">{hoverFeature.address}</p>
                )}
                <p className="text-sm">
                  {STATUSES[hoverFeature.status]} - {TYPES[hoverFeature.type]} -{' '}
                  {hoverFeature.card
                    ? hoverFeature.property_types
                    : tryParseJson(hoverFeature.property_types).join(', ')}
                </p>
              </Popup>
            )}
            {children}
          </>
        )}
      </Map>
    </div>
  )
}
